import { TControlledFormProps } from '@chocolate-soup-inc/cs-frontend-components';
import _ from 'lodash';
import { useCallback, useMemo } from 'react';
import { FieldValues } from 'react-hook-form';
import { toast } from 'react-toastify';
import { getEmployeeFormFields } from '../../../entities/employee/formFields';
import { getEmployeeSchema } from '../../../entities/employee/schema';
import { useQueryAllOffices } from '../../../entities/office/shared';
import { usePrivateCompanyContext } from '../../../routes/outlets/PrivateCompanyOutlet';

export const useEmployeeForm = <T extends FieldValues = FieldValues>() => {
  const company = usePrivateCompanyContext();

  const { data, loading, error } = useQueryAllOffices({
    companyId: company.id
  });

  const offices = useMemo(() => {
    return data?.listOffices.items;
  }, [data?.listOffices.items]);

  const employeeFormFields = useMemo(() => {
    return getEmployeeFormFields<T>({
      company,
      offices
    });
  }, [company, offices]);

  const employeeSchema = useMemo(() => {
    return getEmployeeSchema({
      company,
      offices
    });
  }, [company, offices]);

  const onInvalidSubmit = useCallback<NonNullable<TControlledFormProps<T>['onInvalidSubmit']>>(
    (errors) => {
      const field = employeeFormFields.find((field) => 'name' in field && _.get(errors, field.name));

      if (field && 'name' in field) {
        toast.error(`Please check ${field.label || 'the highlighted fields'} before saving.`);
        const input = document.getElementById(field.name);
        input?.scrollIntoView({ block: 'center' });
        input?.focus();
        return;
      }

      toast.error('Please review the form before saving.');
    },
    [employeeFormFields]
  );

  return {
    employeeFormFields,
    employeeSchema,
    onInvalidSubmit,
    loading: data == null || loading,
    error
  };
};
